import React, { useState, useEffect } from 'react';
import {
  FaSearch,
  FaEye,
  FaSpinner,
  FaArrowLeft,
  FaArrowRight,
  FaClipboardList,
  FaConciergeBell,
  FaBox,
  FaUtensils,
  FaCheckCircle,
  FaClock,
  FaHourglassHalf,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const TrackOrderFrontdesk = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(42);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [selectedTab, setSelectedTab] = useState('all');
  const navigate = useNavigate();

  const toggleExpand = (orderId) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  // Function to get display status based on order type and actual status
  const getDisplayStatus = (orderType, actualStatus) => {
    const status = actualStatus?.toLowerCase() || 'pending';

    // Room orders - show "Delivered" when completed/delivered
    if (orderType === 'room') {
      if (status === 'completed' || status === 'delivered') {
        return 'delivered';
      }
      return status;
    }

    // Dining orders - show "Served" when completed
    if (orderType === 'dining') {
      if (status === 'served') {
        return 'served';
      }
      return status;
    }

    // Parcel orders - show "Delivered" when completed
    if (orderType === 'parcel') {
      if (status === 'delivered') {
        return 'delivered';
      }
      return status;
    }

    return status;
  };

  useEffect(() => {
    const fetchAllOrders = async () => {
      setLoading(true);
      try {
        // Fetch ROOM orders from admin API
        const adminRes = await fetch(`${BQ_BASE_URL}/bq/api/admin_food_order/`, {
          headers: { accept: 'application/json' },
        });

        // Fetch DINING & PARCEL orders from restaurant orders API
        const restaurantRes = await fetch(`${BQ_BASE_URL}/bq/api/restaurant/orders`, {
          headers: { accept: 'application/json' },
        });

        let roomOrders = [];
        let restaurantOrders = [];

        if (adminRes.ok) {
          const adminData = await adminRes.json();
          roomOrders = adminData.filter((order) => order.order_type === 'room') || [];
        }

        if (restaurantRes.ok) {
          const restaurantData = await restaurantRes.json();
          restaurantOrders = restaurantData.orders || [];
        }

        // Transform ROOM orders
        const transformedRoomOrders = roomOrders.map((order) => {
          const actualStatus = order.status?.toLowerCase() || 'pending';
          return {
            id: order.id,
            order_type: 'room',
            customer_name: order.customer_name || 'Guest',
            contact: order.phone_number || '—',
            identifier: `Room ${order.room_number || '—'}`,
            actual_status: actualStatus,
            display_status: getDisplayStatus('room', actualStatus),
            total_amount: order.totalprice || 0,
            created_at: order.created_at,
            kot_number: order.id?.slice(0, 8),
            items: (order.items || []).map((item, idx) => ({
              id: `${order.id}_item_${idx}`,
              name: item.food_title || 'Unknown',
              quantity: item.quantity || 1,
              price: item.food_price || 0,
              item_status: actualStatus,
            })),
          };
        });

        // Transform RESTAURANT orders (Dining & Parcel) from /restaurant/orders API
        const transformedRestaurantOrders = restaurantOrders.map((order) => {
          const orderType = order.order_type || 'dining';
          let identifier = '';

          if (orderType === 'dining') {
            identifier = `Table ${order.table_number || '—'}`;
          } else {
            identifier = `Parcel ${order.parcel_number || '—'}`;
          }

          const actualStatus = order.order_status?.toLowerCase() || 'pending';

          // Group items by kot_number for display (each KOT batch is separate)
          const itemsByKot = {};
          (order.items || []).forEach((item) => {
            const kotNum = item.kot_number || order.kot_number || 'KOT';
            if (!itemsByKot[kotNum]) {
              itemsByKot[kotNum] = [];
            }
            itemsByKot[kotNum].push({
              id: item.item_id,
              name: item.name || 'Unknown',
              quantity: item.quantity || 1,
              price: item.unit_price || 0,
              item_status: item.item_status || actualStatus,
              kot_number: kotNum,
            });
          });

          // For frontdesk view, we want to show all items (including served/delivered)
          // but we'll keep them in their KOT batches
          const allItems = (order.items || []).map((item) => ({
            id: item.item_id,
            name: item.name || 'Unknown',
            quantity: item.quantity || 1,
            price: item.unit_price || 0,
            item_status: item.item_status || actualStatus,
            kot_number: item.kot_number || order.kot_number,
          }));

          return {
            id: order.order_id,
            order_type: orderType,
            customer_name: order.customer_name || 'Guest',
            contact: order.customer_phone || '—',
            identifier: identifier,
            actual_status: actualStatus,
            display_status: getDisplayStatus(orderType, actualStatus),
            total_amount: order.total_amount || 0,
            created_at: order.ordered_at,
            kot_number: order.kot_number,
            parcel_number: order.parcel_number,
            table_number: order.table_number,
            is_paid: order.is_paid,
            items: allItems,
            items_by_kot: itemsByKot,
          };
        });

        const allOrders = [...transformedRoomOrders, ...transformedRestaurantOrders];
        setOrders(allOrders);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllOrders();
  }, []);

  const filterByDate = (order) => {
    if (!order.created_at) return dateFilter === '';
    const orderDate = new Date(order.created_at);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    switch (dateFilter) {
      case 'today':
        return orderDate.toDateString() === today.toDateString();
      case 'yesterday':
        return orderDate.toDateString() === yesterday.toDateString();
      case 'week':
        return orderDate >= startOfWeek;
      case 'month':
        return orderDate >= startOfMonth;
      default:
        return true;
    }
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.id?.toLowerCase().includes(search.toLowerCase()) ||
      (order.customer_name?.toLowerCase() || '').includes(search.toLowerCase()) ||
      order.identifier?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = statusFilter ? order.display_status === statusFilter : true;
    const matchesType = orderTypeFilter ? order.order_type === orderTypeFilter : true;
    const matchesDate = filterByDate(order);

    return matchesSearch && matchesStatus && matchesType && matchesDate;
  });

  // Tab filtering with display statuses
  const tabFilteredOrders = filteredOrders.filter((order) => {
    if (selectedTab === 'all') return true;
    if (selectedTab === 'pending')
      return order.display_status === 'pending' || order.display_status === 'accepted';
    if (selectedTab === 'preparing') return order.display_status === 'preparing';
    if (selectedTab === 'ready') return order.display_status === 'ready';
    if (selectedTab === 'completed')
      return order.display_status === 'served' || order.display_status === 'delivered';
    return true;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = tabFilteredOrders.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(tabFilteredOrders.length / itemsPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const handlePreviousPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  const getVisiblePages = () => {
    const pages = [];
    const pageGroupSize = 10;
    const currentGroup = Math.ceil(currentPage / pageGroupSize);
    const startPage = (currentGroup - 1) * pageGroupSize + 1;
    const endPage = Math.min(startPage + pageGroupSize - 1, totalPages);
    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
    try {
      const date = new Date(dateTimeString);
      return {
        date: date.toLocaleDateString(),
        time: date.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
      };
    } catch {
      return { date: 'N/A', time: 'N/A' };
    }
  };

  // Status config for display
  const getStatusConfig = (displayStatus) => {
    const statusMap = {
      pending: {
        label: 'Pending',
        icon: FaClock,
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        border: 'border-l-amber-500',
      },
      accepted: {
        label: 'Accepted',
        icon: FaCheckCircle,
        color: 'text-blue-600',
        bg: 'bg-blue-50',
        border: 'border-l-blue-500',
      },
      preparing: {
        label: 'Preparing',
        icon: FaHourglassHalf,
        color: 'text-indigo-600',
        bg: 'bg-indigo-50',
        border: 'border-l-indigo-500',
      },
      ready: {
        label: 'Ready',
        icon: FaCheckCircle,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        border: 'border-l-emerald-500',
      },
      served: {
        label: 'Served',
        icon: FaCheckCircle,
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-l-green-500',
      },
      delivered: {
        label: 'Delivered',
        icon: FaCheckCircle,
        color: 'text-green-600',
        bg: 'bg-green-50',
        border: 'border-l-green-500',
      },
      cancelled: {
        label: 'Cancelled',
        icon: FaCheckCircle,
        color: 'text-red-600',
        bg: 'bg-red-50',
        border: 'border-l-red-500',
      },
    };
    return statusMap[displayStatus] || statusMap.pending;
  };

  const getTypeIcon = (type) => {
    const icons = {
      room: FaConciergeBell,
      dining: FaUtensils,
      parcel: FaBox,
    };
    return icons[type] || FaClipboardList;
  };

  const getTypeLabel = (type) => {
    const labels = {
      room: 'Room Service',
      dining: 'Dine In',
      parcel: 'Parcel',
    };
    return labels[type] || type;
  };

  // Tabs with display status counts
  const tabs = [
    {
      id: 'all',
      label: 'All Orders',
      icon: FaClipboardList,
      count: filteredOrders.length,
    },
    {
      id: 'pending',
      label: 'Pending/Accepted',
      icon: FaClock,
      count: filteredOrders.filter(
        (o) => o.display_status === 'pending' || o.display_status === 'accepted'
      ).length,
    },
    {
      id: 'preparing',
      label: 'Preparing',
      icon: FaHourglassHalf,
      count: filteredOrders.filter((o) => o.display_status === 'preparing').length,
    },
    {
      id: 'ready',
      label: 'Ready',
      icon: FaCheckCircle,
      count: filteredOrders.filter((o) => o.display_status === 'ready').length,
    },
    {
      id: 'completed',
      label: 'Delivered/Served',
      icon: FaCheckCircle,
      count: filteredOrders.filter(
        (o) => o.display_status === 'served' || o.display_status === 'delivered'
      ).length,
    },
  ];

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="flex justify-between items-center mb-3 bg-white p-3 rounded-lg shadow-sm">
        <h1 className="text-black text-xl font-bold ml-2">Food & Beverages Management</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-frontdesk')}
            className="btn font-bold text-black"
          >
            Order Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/order-food')}
            className="btn text-gray-600 hover:text-black"
          >
            Order Food
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/dining-tables')}
            className="btn text-gray-600 hover:text-black"
          >
            Dining Tables
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/track-order-kitchen')}
            className="btn text-gray-600 hover:text-black"
          >
            Kitchen Display System(KDS)
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/f&b-management/order-invoicing')}
            className="btn text-gray-600 hover:text-black"
          >
            Billing & Invoicing
          </button>
        </div>
      </header>

      {/* Title Section */}
      <div className="text-center mb-6 mt-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-1">Order Monitoring</h1>
        <p className="text-sm font-poppins font-semibold text-center mb-1 text-gray-600">
          Monitor room service, dine-in, and parcel orders with real-time status tracking and
          operational visibility.
        </p>
        <hr className="border border-gray-400 mt-1 w-64 mx-auto mb-7" />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Orders</p>
              <p className="text-2xl font-semibold text-gray-800">{filteredOrders.length}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <FaClipboardList className="text-gray-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending/Accepted</p>
              <p className="text-2xl font-semibold text-amber-600">
                {
                  filteredOrders.filter(
                    (o) => o.display_status === 'pending' || o.display_status === 'accepted'
                  ).length
                }
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
              <FaClock className="text-amber-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">In Kitchen</p>
              <p className="text-2xl font-semibold text-indigo-600">
                {filteredOrders.filter((o) => o.display_status === 'preparing').length}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center">
              <FaHourglassHalf className="text-indigo-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Ready to Serve</p>
              <p className="text-2xl font-semibold text-emerald-600">
                {filteredOrders.filter((o) => o.display_status === 'ready').length}
              </p>
            </div>
            <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
              <FaCheckCircle className="text-emerald-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-4">
        <div className="flex space-x-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = selectedTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setSelectedTab(tab.id);
                  setCurrentPage(1);
                }}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-b-2 border-gray-800 text-gray-800'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon size={14} />
                <span>{tab.label}</span>
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full ${
                    isActive ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            type="text"
            placeholder="Search by Guest, Table number & Parcel number..."
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <div className="flex gap-2">
          <select
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
            value={orderTypeFilter}
            onChange={(e) => {
              setOrderTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Types</option>
            <option value="room">Room Service</option>
            <option value="dining">Dine In</option>
            <option value="parcel">Parcel</option>
          </select>
          <select
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="preparing">Preparing</option>
            <option value="ready">Ready</option>
            <option value="served">Served</option>
            <option value="delivered">Delivered</option>
          </select>
          <select
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white text-gray-700"
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Dates</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
        </div>
      </div>

      {/* Orders Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <FaSpinner className="animate-spin text-gray-400 text-2xl" />
        </div>
      ) : currentItems.length === 0 ? (
        <div className="  p-16 text-center">
          <div className="max-w-md mx-auto">
            {/* Animated illustration */}
            <div className="relative mb-6">
              <div className="w-32 h-32 bg-gradient-to-br from-green-50 to-emerald-100 rounded-full flex items-center justify-center mx-auto animate-pulse">
                <FaClipboardList className="text-emerald-500 text-5xl" />
              </div>
              <div className="absolute -top-1 right-40 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                <FaCheckCircle className="text-white text-lg" />
              </div>
            </div>

            {/* Main message */}
            <h3 className="text-2xl font-bold text-gray-800 mb-2">All Orders Complete! 🎉</h3>
            <p className="text-gray-600 text-lg mb-2">Kitchen is clean and ready for action</p>
            <div className="w-20 h-1 bg-gradient-to-r from-green-400 to-emerald-500 mx-auto my-4 rounded-full"></div>

            {/* Sub message */}
            <p className="text-gray-500 text-sm">
              New orders will appear here automatically as they come in..!
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {currentItems.map((order) => {
              const { date, time } = formatDateTime(order.created_at);
              const statusConfig = getStatusConfig(order.display_status);
              const StatusIcon = statusConfig.icon;
              const TypeIcon = getTypeIcon(order.order_type);
              const isExpanded = expandedOrderId === order.id;
              const hasItems = order.items && order.items.length > 0;

              // Get the appropriate label for completion status
              const getCompletionLabel = () => {
                if (order.display_status === 'served') return 'Served';
                if (order.display_status === 'delivered') return 'Delivered';
                return statusConfig.label;
              };

              return (
                <div
                  key={order.id}
                  className={`bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden ${statusConfig.border} border-l-4`}
                >
                  <div className="p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <TypeIcon className="text-gray-600 text-sm" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {getTypeLabel(order.order_type)}
                            </span>
                            {order.kot_number && (
                              <>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs font-mono text-gray-500">
                                  {order.kot_number?.slice(0, 8)}
                                </span>
                              </>
                            )}
                            {order.parcel_number && (
                              <>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs font-mono text-gray-500">
                                  {order.parcel_number}
                                </span>
                              </>
                            )}
                          </div>
                          <div className="font-semibold text-gray-800 mt-0.5">
                            {order.identifier}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div
                          className={`flex items-center gap-1.5 px-2 py-1 rounded-full ${statusConfig.bg}`}
                        >
                          <StatusIcon className={`${statusConfig.color} text-xs`} />
                          <span className={`text-xs font-medium ${statusConfig.color}`}>
                            {getCompletionLabel()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Customer Info */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-500">Guest Name:</span>
                        <span className="font-medium text-gray-800">{order.customer_name}</span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-gray-500">Order Time:</span>
                        <span className="text-gray-600">
                          {date}, {time}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm mt-1">
                        <span className="text-gray-500">Total Amount:</span>
                        <span className="font-semibold text-gray-800">
                          ₹{order.total_amount?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                    </div>

                    {/* Items Summary */}
                    {hasItems && (
                      <div className="mb-3">
                        <button
                          onClick={() => toggleExpand(order.id)}
                          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                        >
                          <FaEye size={12} />
                          <span>
                            {isExpanded ? 'Hide Items' : `View ${order.items.length} Items`}
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <div className="flex flex-wrap gap-2">
                              {order.items.map((item, idx) => {
                                const itemStatusConfig = getStatusConfig(item.item_status);
                                return (
                                  <div
                                    key={idx}
                                    className="bg-gray-50 rounded-md px-2 py-1 text-xs"
                                  >
                                    <span className="font-medium text-gray-700">{item.name}</span>
                                    <span className="text-gray-400 mx-1">×</span>
                                    <span className="text-gray-600">{item.quantity}</span>
                                    {item.item_status &&
                                      item.item_status !== order.display_status && (
                                        <span
                                          className={`ml-1 px-1 py-0.5 rounded-full text-[10px] ${itemStatusConfig.bg} ${itemStatusConfig.color}`}
                                        >
                                          {itemStatusConfig.label}
                                        </span>
                                      )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Contact */}
                    {order.contact !== '—' && order.contact && (
                      <div className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
                        Contact: {order.contact}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6 px-2">
              <div className="text-sm text-gray-500">
                Showing {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, tabFilteredOrders.length)}{' '}
                of {tabFilteredOrders.length} orders
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="p-2 rounded-md border border-gray-200 bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <FaArrowLeft size={12} />
                </button>

                {currentPage > 10 && (
                  <>
                    <button
                      onClick={() => handlePageChange(1)}
                      className="px-3 py-1 text-sm rounded-md text-gray-600 hover:bg-gray-100"
                    >
                      1
                    </button>
                    <span className="px-1 text-gray-400">...</span>
                  </>
                )}

                {getVisiblePages().map((page) => (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`px-3 py-1 text-sm rounded-md ${
                      currentPage === page
                        ? 'bg-gray-800 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}

                {currentPage < totalPages - 9 && (
                  <>
                    <span className="px-1 text-gray-400">...</span>
                    <button
                      onClick={() => handlePageChange(totalPages)}
                      className="px-3 py-1 text-sm rounded-md text-gray-600 hover:bg-gray-100"
                    >
                      {totalPages}
                    </button>
                  </>
                )}

                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-md border border-gray-200 bg-white text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <FaArrowRight size={12} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TrackOrderFrontdesk;
